import { AnalyticsBrowser } from '@segment/analytics-next';
import { useMemo } from 'react';
import { useLocalStorage } from 'react-use';

import { getGrowUniversalId } from '@growtherapy/analytics';

import { isDevMode } from '../config';
import { UserType } from '../types';
import { getSegmentConfig } from './config';
import {
  EventTypeEnum,
  FormSubmittedParams,
  SegmentTrackMetricEnum,
  TrackParams,
  UserClickedParams,
  UserInputtedParams,
  UserViewedParams,
} from './types';
import { isAutomatedUser } from './utils';

export const SHOULD_LOG_SEGMENT_EVENTS_KEY = 'shouldLogSegmentEvents';

const { segmentWriteKey } = getSegmentConfig();

let segmentAnalytics: AnalyticsBrowser;
let segmentUserType: UserType | undefined = undefined;
const anonymousId: string | undefined = undefined;
const growUniversalId = getGrowUniversalId();

export const loadSegment = (userType: UserType, anonymousId?: string): void => {
  if (isAutomatedUser(anonymousId)) return;
  const analytics = AnalyticsBrowser.load(
    { writeKey: segmentWriteKey },
    { storage: { stores: ['cookie', 'localStorage', 'memory'] } },
  );

  analytics.setAnonymousId(anonymousId);
  segmentAnalytics = analytics;
  segmentUserType = userType;
};

export const analytics = {
  identify: (userId?: string, traits: Record<string, unknown> = {}, options: Record<string, unknown> = {}): void => {
    if (!segmentAnalytics) return;
    segmentAnalytics.identify(userId, traits, {
      anonymousId,
      ...options,
    });
  },
  page: (name: string, properties: Record<string, unknown> = {}, options: Record<string, unknown> = {}): void => {
    if (!segmentAnalytics) return;
    segmentAnalytics.page(undefined, name, properties, {
      anonymousId,
      ...options,
    });
  },
  track: (
    eventName: SegmentTrackMetricEnum,
    properties: Record<string, unknown>,
    options: { shouldLog?: boolean } & Record<string, unknown> = {},
  ): void => {
    if (!segmentAnalytics) return;

    const { shouldLog = false, ...passedOptions } = options;

    const trackOptions = {
      anonymousId,
      userType: segmentUserType,
      growUniversalId: growUniversalId,
      ...passedOptions,
    };

    if (shouldLog) {
      // eslint-disable-next-line no-console
      console.log(`[Segment] Tracking ${eventName} event`, properties, trackOptions);
    }

    segmentAnalytics.track(eventName, properties, trackOptions);
  },
};

function trackEvent<P extends EventTypeEnum>(
  eventName: SegmentTrackMetricEnum,
  type: P,
  properties: Partial<TrackParams>,
  shouldLog = false,
): void {
  if (!segmentAnalytics || !type) return;

  analytics.track(
    eventName,
    {
      type,
      userType: segmentUserType,
      ...properties,
    },
    { shouldLog },
  );
}

export const useTrackEvent = () => {
  const [_shouldLog] = useLocalStorage('shouldLogSegmentEvents', false);
  const shouldLog = (isDevMode && _shouldLog) ?? false;

  return useMemo(
    () => ({
      userViewed: <P extends EventTypeEnum>(type: NonNullable<P>, properties: UserViewedParams<P>): void => {
        trackEvent(SegmentTrackMetricEnum.USER_VIEWED, type, properties, shouldLog);
      },
      userClicked: <P extends EventTypeEnum>(type: NonNullable<P>, properties: UserClickedParams<P>): void => {
        trackEvent(SegmentTrackMetricEnum.USER_CLICKED, type, properties, shouldLog);
      },
      userInputted: <P extends EventTypeEnum>(type: NonNullable<P>, properties: UserInputtedParams<P>): void => {
        trackEvent(SegmentTrackMetricEnum.USER_INPUTTED, type, properties, shouldLog);
      },
      formSubmitted: <P extends EventTypeEnum>(type: NonNullable<P>, properties: FormSubmittedParams<P>): void => {
        trackEvent(SegmentTrackMetricEnum.FORM_SUBMITTED, type, properties, shouldLog);
      },
    }),
    [shouldLog],
  );
};
